import { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import {
  ExclamationCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ServerIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid"
import ParamEditDialog from "aldoo-ra/Admin/ParamIO/components/ParamEditDialog"
import { GetParam } from "aldoo-ra/Admin/ParamIO/api"
import { GetRelayStatus, TriggerRelaySetup } from "aldoo-ra/Admin/CustomerSupport/api"

const StatusBadge = ({ status }) => {
  if (status === "ok")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
        <CheckCircleIconSolid className="w-3.5 h-3.5" /> Configured
      </span>
    )
  if (status === "missing")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
        <ExclamationCircleIcon className="w-3.5 h-3.5" /> Missing
      </span>
    )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-admin_neutral_1 text-admin_text_secondary text-xs font-medium">
      <ClockIcon className="w-3.5 h-3.5" /> Unknown
    </span>
  )
}

const Step = ({ number, title, icon: Icon, status, onConfigure, children }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
          status === "ok"
            ? "bg-green-100 text-green-700"
            : "bg-admin_neutral_1 text-admin_text_secondary"
        }`}
      >
        {status === "ok" ? (
          <CheckCircleIconSolid className="w-5 h-5 text-green-600" />
        ) : (
          number
        )}
      </div>
      <div className="w-px flex-1 bg-admin_bordered mt-2" />
    </div>
    <div className="pb-8 flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-admin_text_secondary shrink-0" />}
        <Typography variant="admin_body_accent" className="my-0">
          {title}
        </Typography>
        {status && <StatusBadge status={status} />}
        {status === "missing" && onConfigure && (
          <Button variant="admin_primary" onClick={onConfigure}>
            Configure
          </Button>
        )}
        {status === "ok" && onConfigure && (
          <button
            onClick={onConfigure}
            className="text-admin_text_secondary hover:text-admin_primary transition-colors"
            title="Edit"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="text-sm text-admin_text_secondary space-y-2">{children}</div>
    </div>
  </div>
)

const CodeBlock = ({ children }) => (
  <pre className="bg-admin_canvas border border-admin_bordered rounded-md px-4 py-3 text-xs font-mono text-admin_text overflow-x-auto">
    {children}
  </pre>
)

const CSSettings = () => {
  const [relayStatus, setRelayStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingParam, setEditingParam] = useState(null)
  const [setupRunning, setSetupRunning] = useState(false)
  const [setupResult, setSetupResult] = useState(null)

  const fetchStatus = () => {
    GetRelayStatus()
      .then((data) => setRelayStatus(data))
      .catch(() => setRelayStatus(null))
      .finally(() => setLoading(false))
  }

  const triggerSetup = () => {
    setSetupRunning(true)
    setSetupResult(null)
    TriggerRelaySetup()
      .then((data) => {
        setSetupResult(data)
        setLoading(true)
        fetchStatus()
      })
      .catch((err) => setSetupResult({ result: false, error: err.message }))
      .finally(() => setSetupRunning(false))
  }

  useEffect(() => { fetchStatus() }, [])

  const openParam = async (address) => {
    const result = await GetParam(address)
    if (result?.address) {
      setEditingParam(result)
    } else {
      // Param doesn't exist yet — open create dialog pre-filled with the address
      setEditingParam({ address, value: "", description: "" })
    }
  }

  const cfg = relayStatus?.config || {}

  const stepStatus = (key) => {
    if (loading) return "unknown"
    return cfg[key] ? "ok" : "missing"
  }

  const allConfigured =
    !loading &&
    cfg.apiKey &&
    cfg.fromEmail &&
    cfg.replyToEmail &&
    cfg.domain &&
    cfg.apiDomain &&
    cfg.mockEmailDisabled

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <Cog6ToothIcon className="w-6 h-6 text-admin_text_secondary" />
        <Typography variant="admin_h2" className="my-0">
          Email Relay Settings
        </Typography>
      </div>
      <Typography variant="admin_body_dim" className="mb-8">
        When a support agent replies to a ticket, the user receives an email. When
        the user replies to that email, it is automatically routed back into the
        ticket. The steps below configure this full round-trip flow via SparkPost.
      </Typography>

      {allConfigured && (
        <div className="mb-8 flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircleIconSolid className="w-5 h-5 text-green-600 shrink-0" />
          <Typography variant="admin_body" className="my-0 text-green-800">
            All configuration steps are complete. Email relay is active.
          </Typography>
        </div>
      )}

      <div className="space-y-0">
        {/* Step 1 */}
        <Step
          number={1}
          title="SparkPost API Key"
          icon={WrenchScrewdriverIcon}
          status={stepStatus("apiKey")}
          onConfigure={() => openParam("SPARK_POST_API_KEY")}
        >
          <p>
            The key needs <strong>Relay Webhooks: Read/Write</strong> and{" "}
            <strong>Inbound Domains: Read/Write</strong> permissions.
          </p>
          <CodeBlock>{`SPARK_POST_API_KEY: "${cfg.apiKey ? "••••••••••••••••" : "your-sparkpost-api-key"}"`}</CodeBlock>
        </Step>

        {/* Step 2 */}
        <Step
          number={2}
          title="From Email"
          icon={EnvelopeIcon}
          status={stepStatus("fromEmail")}
          onConfigure={() => openParam("SPARK_POST_MAIL_FROM_EMAIL")}
        >
          <p>
            The sender email address used when sending replies to users.
            Must be a verified sending domain in SparkPost.
          </p>
          <CodeBlock>{`SPARK_POST_MAIL_FROM_EMAIL: "${cfg.fromEmail || "support@pulse.aldoo.com"}"`}</CodeBlock>
        </Step>

        {/* Step 3 */}
        <Step
          number={3}
          title="From Name"
          icon={EnvelopeIcon}
          status={stepStatus("fromName")}
          onConfigure={() => openParam("SPARK_POST_MAIL_FROM_NAME")}
        >
          <p>
            The display name shown to users in the From field of the email.
          </p>
          <CodeBlock>{`SPARK_POST_MAIL_FROM_NAME: "${cfg.fromName || "Pulse Support"}"`}</CodeBlock>
        </Step>

        {/* Step 4 */}
        <Step
          number={4}
          title="Reply-To Email (SPARK_POST_REPLY_TO_EMAIL)"
          icon={EnvelopeIcon}
          status={stepStatus("replyToEmail")}
          onConfigure={() => openParam("SPARK_POST_REPLY_TO_EMAIL")}
        >
          <p>
            The address users reply to. Must be on the inbound relay domain
            configured in step 5. When a user hits Reply, SparkPost captures the
            email and forwards it to the webhook.
          </p>
          <CodeBlock>{`SPARK_POST_REPLY_TO_EMAIL: "${cfg.replyToEmail || "support@agent.pulse.aldoo.com"}"`}</CodeBlock>
          {cfg.replyToEmail && (
            <p className="text-green-700 font-medium">
              Currently set to: <code className="bg-green-50 px-1 rounded">{cfg.replyToEmail}</code>
            </p>
          )}
        </Step>

        {/* Step 5 */}
        <Step
          number={5}
          title="Inbound Relay Domain (SPARK_POST_DOMAIN)"
          icon={GlobeAltIcon}
          status={stepStatus("domain")}
          onConfigure={() => openParam("SPARK_POST_DOMAIN")}
        >
          <p>
            The subdomain that receives inbound email. SparkPost will register this
            as an inbound domain and set up the relay webhook automatically on
            server startup.
          </p>
          <CodeBlock>{`SPARK_POST_DOMAIN: "${cfg.domain || "agent.pulse.aldoo.com"}"`}</CodeBlock>
          {cfg.domain && (
            <p className="text-green-700 font-medium">
              Currently set to: <code className="bg-green-50 px-1 rounded">{cfg.domain}</code>
            </p>
          )}
          <p className="mt-2">
            Add an <strong>MX record</strong> in your DNS for this subdomain pointing
            to SparkPost's inbound mail server:
          </p>
          <CodeBlock>{`${cfg.domain || "agent.pulse.aldoo.com"}.  MX  10  rx1.eu.sparkpostmail.com.`}</CodeBlock>
          <p className="text-xs text-admin_text_secondary">
            Use <code>rx1.eu.sparkpostmail.com</code> for EU accounts or{" "}
            <code>rx1.sparkpostmail.com</code> for US accounts. Verify in your
            SparkPost dashboard under <strong>Configuration → Inbound Domains</strong>.
          </p>
        </Step>

        {/* Step 6 */}
        <Step
          number={6}
          title="Public API Domain (API_DOMAIN)"
          icon={ServerIcon}
          status={stepStatus("apiDomain")}
          onConfigure={() => openParam("API_DOMAIN")}
        >
          <p>
            The public URL of this API server. SparkPost posts inbound emails to:
          </p>
          <CodeBlock>{`{API_DOMAIN}/v1/customer-support/webhook/inbound-email`}</CodeBlock>
          {cfg.apiDomain && (
            <p className="text-green-700 font-medium">
              Webhook target:{" "}
              <code className="bg-green-50 px-1 rounded">
                {cfg.apiDomain}/v1/customer-support/webhook/inbound-email
              </code>
            </p>
          )}
          <p>
            Must be a publicly reachable HTTPS URL — <code>localhost</code> will
            not work in production.
          </p>
          <CodeBlock>{`API_DOMAIN: "${cfg.apiDomain || "https://api.pulse.aldoo.com"}"`}</CodeBlock>
        </Step>

        {/* Step 7 */}
        <Step
          number={7}
          title="Disable Mock Email Sending"
          icon={EnvelopeIcon}
          status={stepStatus("mockEmailDisabled")}
          onConfigure={() => openParam("MOCK_EMAIL_SENDING")}
        >
          <p>
            Mock email mode is enabled by default in development. Set it to{" "}
            <code className="bg-admin_canvas px-1 rounded">false</code> for emails
            to actually be sent via SparkPost.
          </p>
          <CodeBlock>{`MOCK_EMAIL_SENDING: ${cfg.mockEmailDisabled ? "false" : "true"}`}</CodeBlock>
        </Step>

        {/* Step 8 — auto setup */}
        <Step
          number={8}
          title="Relay Webhook Auto-Setup"
          icon={Cog6ToothIcon}
          status={relayStatus?.webhookExists ? "ok" : loading ? "unknown" : "missing"}
        >
          <p>
            On every server startup, the system automatically:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Registers the inbound domain with SparkPost (if not already registered)</li>
            <li>Creates a relay webhook pointing to the inbound email endpoint</li>
          </ul>
          <p className="mt-2">
            This requires steps 1, 5, and 6 to be complete first. You can also
            trigger setup manually without restarting the server.
          </p>
          {relayStatus?.webhookExists && (
            <p className="text-green-700 font-medium mt-1">
              Relay webhook is registered in SparkPost.
            </p>
          )}
          {!loading && !relayStatus?.webhookExists && cfg.apiKey && cfg.domain && (
            <div className="space-y-2 mt-1">
              <p className="text-amber-600 font-medium">
                Webhook not yet registered.
              </p>
              <Button
                variant="admin_primary"
                onClick={triggerSetup}
                disabled={setupRunning}
              >
                {setupRunning ? "Setting up…" : "Trigger Setup Now"}
              </Button>
              {setupResult && (
                <pre className="bg-admin_canvas border border-admin_bordered rounded-md px-4 py-3 text-xs font-mono text-admin_text overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(setupResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </Step>

        {/* Step 9 — email template, no configure button (managed in Mailer) */}
        <Step
          number={9}
          title="Email Template: customer-support-agent-reply"
          icon={EnvelopeIcon}
          status={relayStatus?.templateExists ? "ok" : loading ? "unknown" : "missing"}
        >
          <p>
            The CMS mail template used when an agent replies to a ticket. It must
            exist in <strong>Admin → Mailer → Templates</strong> with the ID{" "}
            <code className="bg-admin_canvas px-1 rounded">customer-support-agent-reply</code>.
          </p>
          <p>Available template variables:</p>
          <CodeBlock>{`{{PRODUCT_NAME}}     — App name
{{TICKET_ID}}        — Ticket ID (used for reply threading)
{{SUMMARY}}          — Original ticket summary
{{AGENT_NAME}}       — Replying agent's name
{{MESSAGE_BODY}}     — The reply message
{{FILE_ATTACHMENTS}} — HTML links to any attached files
{{AGENT_SIGNATURE}}  — Agent signature from CMS (CSAgentSignature)
{{SUPPORT_EMAIL}}    — The reply-to address (SPARK_POST_REPLY_TO_EMAIL)`}</CodeBlock>
          <p className="text-xs text-admin_text_secondary">
            The <code>{"{{TICKET_ID}}"}</code> must appear in the email subject line so
            that user replies can be matched back to the correct ticket.
          </p>
        </Step>
      </div>

      {editingParam && (
        <ParamEditDialog
          param={editingParam}
          mode={editingParam.address && !editingParam._id ? "create" : "edit"}
          onClose={(shouldRefresh) => {
            setEditingParam(null)
            if (shouldRefresh) {
              setLoading(true)
              fetchStatus()
            }
          }}
          onRefresh={() => {
            setEditingParam(null)
            setLoading(true)
            fetchStatus()
          }}
        />
      )}
    </div>
  )
}

export default CSSettings
